<template>
  <b-form ref="groupForm" @submit="onSubmit" class="needs-validation">
    <b-col lg="4">
      <b-form-group id="groupType">
        <span class="fieldHeader">{{$t('privatePage.customerType_header')}}:</span>
        <b-form-radio-group id="supplierCustomersRadio"
                            :options="availableCustomerTypes"
                            v-model="currentGroup.userGroupTypeId"
                            value-field="id"
                            text-field="name"
                            v-if="hasSupplierPermissions && !currentGroup.id"
                            novalidate>
        </b-form-radio-group>
        <span v-else>
          {{currentGroup.userGroupType}}
        </span>
      </b-form-group>
      <b-form-group id="groupName">
        <span class="fieldHeader">{{$t('name')}}:</span>
        <b-form-input id="nameInput"
                      type="text"
                      required
                      :state="stateName"
                      v-model="currentGroup.name">
        </b-form-input>
        <template slot="invalid-feedback">
          {{feedbackName}}
        </template>
      </b-form-group>

      <b-form-group id="orgNumber">
        <span class="fieldHeader">{{$t('privatePage.orgNumber')}}:</span>
        <b-form-input id="orgNumberInput"
                      type="text"
                      v-model="currentGroup.organisationNumber"
                      required
                      :state="stateOrgNumber">
        </b-form-input>
        <template slot="invalid-feedback">
          {{feedbackOrganisationNumber}}
        </template>
      </b-form-group>

      <b-form-group id="credits">
        <span class="fieldHeader">{{$t('privatePage.credits_header')}}:</span>
        <b-form-input id="creditsInput"
                      type="number"
                      v-model="currentGroup.credits"
                      required
                      :state="stateCredits">
        </b-form-input>
        <template slot="invalid-feedback">
          {{feedbackCredits}}
        </template>
      </b-form-group>

      <b-form-group id="imageForm">
        <span class="fieldHeader">{{$t('logo')}}:</span>
        <input type="file"
          :placeholder="$t('privatePage.logoChoice', { logo: '(jpg/png/gif)' })"
          accept="image/jpeg, image/png, image/gif"
          @change="onFileChange"
        />
      </b-form-group>

      <b-form-group id="urlTagGroup" v-show="showTag">
        <span class="fieldHeader">{{$t('privatePage.urlTag')}}:</span>
        <b-form-input id="urlTagInput"
                      type="text"
                      v-model="currentGroup.urlTag"
                      :state="stateUrlTag">
        </b-form-input>
        <template slot="invalid-feedback">
          {{feedbackUrlTag}}
        </template>
        <b-form-text id="inputLiveHelp">
          ({{$t('example')}}: <span class="exampleUrl">{{urlExample}}</span> = {{urlExamplePre}}<span class="exampleUrl">{{urlExample}}</span>)
        </b-form-text>
      </b-form-group>

      <b-form-group id="activeGroup"
                    v-if="currentGroup.id">
        <b-form-checkbox id="activeCheckbox"
                        v-model="currentGroup.isActive"
                        v-if="currentGroup.id">
        {{ $t('privatePage.isActive') }}
        </b-form-checkbox>
      </b-form-group>
      <b-form-group id="testInterpreterPermissionsGroup">
        <b-form-checkbox id="testInterpreterPermissionsCheckbox"
                        v-model="currentGroup.hasTestInterpreterPermissions">
        {{ $t('privatePage.usergroup_has_test_interpreter_permissions') }}
        </b-form-checkbox>
      </b-form-group>
      <b-form-group id="teamprofileGroup">
        <b-form-checkbox id="teamprofileCheckbox"
                        v-model="saveTeamProfiles">
          {{ $t('privatePage.teamprofile_change') }}
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-button type="submit" class="me-1">{{ $t('save') }}</b-button>
        <b-button type="button" variant="danger" @click.stop="deleteGroup">{{ $t('delete') }}</b-button>
        <div class="mt-2">
          <b-alert v-model="alerts.countdown"
                  dismissible
                  :variant="alerts.variant"
                  @closed="alerts.countdown=0"
                  @close-count-down="countDownChanged">
            {{alerts.message}}
          </b-alert>
        </div>
      </b-form-group>
    </b-col>
  </b-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'user-group-detail-editor',
  computed: {
    ...mapGetters('userGroupsModule', [
      'currentGroup',
      'defaultCustomerTypeId',
      'defaultCustomerTypeName',
      'customerTypes'
    ]),
    ...mapGetters([
      'languages',
      'userId',
      'selectedCulture',
      'hasSupplierPermissions'
    ]),
    stateName() {
      if (this.currentGroup && this.currentGroup.name) {
        return this.currentGroup.name.length > 0
      }
      return false
    },
    stateUrlTag() {
      if (this.currentGroup && this.currentGroup.urlTag) {
        var tagRegExp = new RegExp('^[0-9a-zA-Z]*$')
        var field = this.currentGroup.urlTag
        var valid = tagRegExp.test(field)
        return valid
      }

      return true
    },
    stateOrgNumber() {
      if (this.currentGroup && this.currentGroup.organisationNumber) {
        var field = this.currentGroup.organisationNumber
        var orgRegExp = new RegExp('^[0-9]{6}[-][0-9]{4}$')
        var valid = orgRegExp.test(field)
        return valid
      }
      return false
    },
    stateCredits() {
      if (this.currentGroup && this.currentGroup.credits !== undefined) {
        if (Number.isInteger(this.currentGroup.credits)) {
          return true
        }
        return this.currentGroup.credits.length > 0
      }

      return false
    },
    feedbackName() {
      return this.$t('name_required_feedback')
    },
    submitMessage() {
      return this.$t('save_ok_feedback')
    },
    deleteConfirmationMessage() {
      return this.$t('privatePage.groupDelete_confirmation')
    },
    deleteMessage() {
      return this.$t('privatePage.groupDelete_message')
    },
    deleteErrorMessage() {
      return this.$t('privatPage.groupDelete_error_message')
    },
    feedbackOrganisationNumber() {
      return this.$t('privatePage.orgNumberFormat', { format: '000000-0000' })
    },
    feedbackCredits() {
      return this.$t('privatePage.credits_feedback')
    },
    feedbackUrlTag() {
      return this.$t('privatePage.urlTag_feedback')
    },
    urlExamplePre() {
      return window.location.origin + '/?'
    },
    urlExample() {
      if (this.currentGroup && this.currentGroup.name) {
        var name = this.currentGroup.name
        var placeholder = name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
        return placeholder
      }
      return ''
    },
    showTag() {
      return (
        (this.currentGroup &&
          this.currentGroup.imageUrl &&
          this.currentGroup.imageUrl.length > 0) ||
        (this.logo && this.logo.name && this.logo.name.length > 0)
      )
    },
    availableCustomerTypes() {
      return this.customerTypes.filter(c => c.id !== 1)
    }
  },
  methods: {
    ...mapActions('userGroupsModule', {
      saveGroupDetails: 'saveGroupDetails',
      selectGroup: 'selectGroup',
      deleteGroup: 'deleteGroup',
      updateUserPermissions: 'updateUserPermissions',
      getUserById: 'getUserById'
    }),
    onFileChange: function(e) {
      const files = e.target.files
      if (!files.length) return
      this.logo = files[0]
    },
    onSubmit: function(evt) {
      if (this.validateForm()) {
        evt.preventDefault()
        let group = this.currentGroup
        let fData = new FormData()

        fData.append('name', group.name)
        fData.append('organisationNumber', group.organisationNumber)
        fData.append('credits', group.credits)
        fData.append('ownerId', group.id ? group.ownerId : this.userId)
        fData.append('isActive', group.id ? group.isActive : true)
        fData.append(
          'hasTestInterpreterPermissions',
          group.hasTestInterpreterPermissions
        )
        fData.append('userGroupTypeId', group.userGroupTypeId)
        fData.append('logo', this.logo.name ? this.logo : '')
        fData.append('urlTag', group.urlTag ? group.urlTag : '')

        this.saveGroupDetails({
          fData: fData,
          groupId: this.currentGroup.id,
          userId: this.userId,
          logo: this.logo
        })
          .then(() => {
            this.showSuccessMessage(this.submitMessage)
          })
          .catch(error => {
            this.showErrorMessage(error)
          })

        // Team profile value has changed. Save it!
        // For now, all users in a user group have the same permissions
        const user = this.customerUser
        const permissions = (user && user.permissions) || []
        if (!!(permissions.indexOf('TeamReports') !== -1) !== this.saveTeamProfiles) {
          const newPermissions = this.saveTeamProfiles ? [...permissions, 'TeamReports'] : [...permissions].filter(item => item !== 'TeamReports')
          this.submitPermissions({ permissions: newPermissions })
        }
      }
    },
    submitPermissions: function(permissions) {
      const groupUsers = (this.currentGroup && this.currentGroup.members) || []
      groupUsers.map(user => {
        this.updateUserPermissions({ userId: user.id, permissions: permissions })
          .then(result => {
            if (result.ok === true) {
              this.showSuccessMessage(this.$t('save_ok_feedback'))
              this.fetchUserPermissions()
            } else {
              this.showErrorMessage(result.message)
            }
          })
          .catch(() => {})
      })
    },
    validateForm: function() {
      return (
        this.stateName &&
        this.stateUrlTag &&
        this.stateOrgNumber &&
        this.stateCredits
      )
    },
    countDownChanged: function(dismissCountDown) {
      this.alerts.countdown = dismissCountDown
    },
    showErrorMessage: function(message) {
      this.alerts.message = message
      this.alerts.countdown = this.alerts.dismissSecs
      this.alerts.variant = 'danger'
    },
    showMessage: function(message) {
      this.alerts.message = message
      this.alerts.countdown = this.alerts.dismissSecs
      this.alerts.variant = 'info'
    },
    showSuccessMessage: function(message) {
      this.alerts.message = message
      this.alerts.countdown = this.alerts.dismissSecs
      this.alerts.variant = 'success'
    },
    deleteGroup: function() {
      if (window.confirm(this.deleteConfirmationMessage)) {
        this.deleteGroup(this.currentGroup)
          .then(result => {
            if (result.ok) {
              window.alert(this.deleteMessage)
              this.$router.push('/private/usergroups')
            } else {
              this.showErrorMessage(this.deleteErrorMessage)
            }
          })
          .catch(error => {
            this.showErrorMessage(error)
          })
      }
    },
    fetchUserPermissions: function() {
      const group = this.currentGroup
      if (group && group.members && group.members.length > 0) {
        this.getUserById(group.members[0].id).then(user => {
          const permissions = user && user.permissions
          this.saveTeamProfiles = !!(permissions && permissions.indexOf('TeamReports') !== -1)
          this.customerUser = user
        })
      }
    }
  },
  data() {
    return {
      alerts: {
        dismissSecs: 3,
        countdown: 0,
        message: '',
        variant: 'warning'
      },
      logo: {},
      saveTeamProfiles: false,
      customerUser: {}
    }
  },
  created() {
    this.fetchUserPermissions()
    if (
      !(
        typeof this.currentGroup !== 'undefined' &&
        typeof this.currentGroup.userGroupTypeId !== 'undefined'
      )
    ) {
      this.selectGroup({
        id: '',
        credits: 0,
        imageUrl: '',
        members: [],
        name: '',
        organisationNumber: '',
        ownerId: this.userId,
        userGroupType: this.defaultCustomerTypeName,
        userGroupTypeId: this.defaultCustomerTypeId,
        isActive: true,
        hasTestInterpreterPermissions: true,
        urlTag: ''
      })
    }
  }
}
</script>

<style scoped>
.exampleUrl {
  font-weight: bolder;
}
</style>
